/**
 * CoverInspectorDialog — admin tool for inspecting + repairing
 * server-side Plex cover cache, scoped to one playlist's provider.
 *
 * Three things this dialog does:
 *   1. Surface a tab-filtered list of covers (all / cached / failed
 *      / corrupted) so the admin can see exactly what's on disk.
 *   2. "Retry failed" button — re-fetches every `status=failed` row
 *      from the upstream Plex transcoder. Most transient hiccups
 *      recover on a second hit.
 *   3. "Delete corrupted" button — scans every cached row, sanity-
 *      checks the on-disk bytes (size + magic-number sniff) and
 *      removes anything that doesn't look like a real image. The
 *      next harvest will then re-fetch fresh copies.
 *
 * Lives behind the row's "covers" stat icon in Admin → Playlists.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  RefreshCw,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  listCovers,
  retryFailedCovers,
  deleteCorruptedCovers,
  deleteFailedCovers,
  deleteCover,
  testCoverUrl,
} from "../lib/api";

const TABS = [
  { key: "all", label: "All" },
  { key: "cached", label: "Passed" },
  { key: "failed", label: "Failed" },
  { key: "corrupted", label: "Corrupted" },
];

export default function CoverInspectorDialog({ open, onOpenChange, playlist }) {
  const { toast } = useToast();
  const [tab, setTab] = useState("failed");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // "Test cover URL" diagnostic — paste any Plex URL, hit Test,
  // see status/size/hash/cached/whether-it-looks-like-an-image.
  // Useful when the provider rotates the X-Plex-Token and we want
  // to find out WHICH urls are still good.
  const [testUrl, setTestUrl] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function reload() {
    if (!playlist) return;
    setLoading(true);
    try {
      const res = await listCovers(playlist.id, { status: tab, limit: 50 });
      setRows(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast({
        title: "Could not load covers",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) reload();
  }, [open, tab, playlist?.id]);

  async function onRetryFailed() {
    setBusy(true);
    try {
      const res = await retryFailedCovers(playlist.id);
      toast({
        title: "Retry started",
        description:
          res.status === "completed"
            ? `Refetched ${res.cached} / ${res.total_urls} (${res.failed} still failed)`
            : "Running in background — list refreshes every few seconds.",
      });
    } catch (e) {
      toast({
        title: "Retry failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      // Refresh list after a short delay so background results land.
      setTimeout(reload, 1500);
    }
  }

  async function onDeleteCorrupted() {
    setBusy(true);
    try {
      const res = await deleteCorruptedCovers(playlist.id);
      toast({
        title: "Corrupted covers cleared",
        description: `Deleted ${res.deleted} of ${res.total_checked} scanned. ` +
          `Reasons: ${Object.entries(res.reasons || {})
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ") || "none"}`,
      });
    } catch (e) {
      toast({
        title: "Could not delete corrupted",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setTimeout(reload, 800);
    }
  }

  async function onDeleteFailed() {
    if (
      !window.confirm(
        "Bulk-delete every failed cover entry for this provider? " +
          "Next harvest will start fresh on these URLs. Continue?",
      )
    )
      return;
    setBusy(true);
    try {
      const res = await deleteFailedCovers(playlist.id);
      toast({
        title: "Failed covers deleted",
        description: `Removed ${res.deleted} `+
          `failed entries for ${res.provider_host || "this provider"}.`,
      });
    } catch (e) {
      toast({
        title: "Could not delete failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setTimeout(reload, 800);
    }
  }

  async function onDeleteOne(h) {
    try {
      await deleteCover(h);
      toast({ title: "Cover deleted", description: h.slice(0, 12) + "…" });
      reload();
    } catch (e) {
      toast({
        title: "Could not delete",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  async function onTestUrl() {
    if (!testUrl.trim()) return;
    setTestBusy(true);
    setTestResult(null);
    try {
      const res = await testCoverUrl(testUrl.trim());
      setTestResult(res);
    } catch (e) {
      toast({
        title: "Test failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setTestBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-cover-inspector"
        className="max-w-4xl max-h-[90vh] flex flex-col"
      >
        <DialogHeader>
          <DialogTitle>Cover Cache — {playlist?.name || "—"}</DialogTitle>
          <DialogDescription>
            Provider:{" "}
            <code className="text-xs">
              {playlist?.m3u_url
                ? new URL(playlist.m3u_url.replace(/^b2c:\/\//, "http://")).host
                : "—"}
            </code>{" "}
            · Covers are shared across every playlist on the same provider.
          </DialogDescription>
        </DialogHeader>

        {/* Tab bar + action buttons. */}
        <div className="flex items-center gap-2 flex-wrap border-b pb-3 mb-3">
          {TABS.map((t) => (
            <Button
              key={t.key}
              size="sm"
              variant={tab === t.key ? "default" : "outline"}
              onClick={() => setTab(t.key)}
              data-testid={`cover-tab-${t.key}`}
            >
              {t.label}
              {tab === t.key && total !== 0 ? (
                <Badge variant="secondary" className="ml-2 text-[10px]">
                  {total}
                </Badge>
              ) : null}
            </Button>
          ))}
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onRetryFailed}
            data-testid="cover-retry-failed"
            className="text-amber-700 border-amber-300 hover:bg-amber-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
            Retry failed
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onDeleteCorrupted}
            data-testid="cover-delete-corrupted"
            className="text-red-700 border-red-300 hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Delete corrupted
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onDeleteFailed}
            data-testid="cover-delete-failed"
            className="text-red-700 border-red-300 hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Delete failed
          </Button>
        </div>

        {/* Test cover URL — diagnostic for provider token rotations. */}
        <div className="flex items-center gap-2 mb-3 p-2.5 rounded-md bg-slate-50 border border-slate-200">
          <input
            data-testid="cover-test-input"
            type="text"
            placeholder="Paste a Plex (or any image) URL to test reachability…"
            value={testUrl}
            onChange={(e) => setTestUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onTestUrl()}
            className="flex-1 px-3 py-1.5 text-xs rounded border border-slate-300 bg-white focus:outline-none focus:border-emerald-400"
          />
          <Button
            data-testid="cover-test-run"
            size="sm"
            disabled={testBusy || !testUrl.trim()}
            onClick={onTestUrl}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {testBusy ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : null}
            Test
          </Button>
        </div>
        {testResult ? (
          <div
            data-testid="cover-test-result"
            className="text-[11px] mb-3 p-3 rounded-md bg-slate-900 text-emerald-200 font-mono space-y-0.5"
          >
            <div className="flex items-center gap-2 mb-1">
              {testResult.is_image ? (
                <Badge className="bg-emerald-600 text-[10px]">ok</Badge>
              ) : (
                <Badge variant="destructive" className="text-[10px]">
                  {testResult.error ? "error" : "not-an-image"}
                </Badge>
              )}
              <span className="text-white">
                HTTP {testResult.http_status ?? "—"} · {testResult.duration_ms} ms ·{" "}
                {testResult.size_bytes
                  ? `${(testResult.size_bytes / 1024).toFixed(1)} KB`
                  : "0 KB"}
              </span>
              {testResult.cached_on_disk ? (
                <Badge variant="secondary" className="text-[10px]">
                  cached
                </Badge>
              ) : null}
            </div>
            <div>
              <span className="text-slate-400">hash:</span> {testResult.hash}
            </div>
            <div>
              <span className="text-slate-400">content-type:</span>{" "}
              {testResult.content_type || "—"}
            </div>
            <div className="break-all">
              <span className="text-slate-400">transcode:</span>{" "}
              {testResult.transcode_url}
            </div>
            {testResult.error ? (
              <div className="text-red-400">
                <span className="text-slate-400">error:</span> {testResult.error}
              </div>
            ) : null}
            {testResult.db_status ? (
              <div>
                <span className="text-slate-400">db:</span> {testResult.db_status}
                {testResult.db_error ? ` (${testResult.db_error})` : ""}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Body. */}
        <div className="overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-500">
              <CheckCircle2 className="w-6 h-6 text-emerald-500 mb-2" />
              No covers in this tab — nothing to do.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">Preview</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead className="w-24">Size</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-16 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const isCached = r.status === "cached" && !r.reason;
                  return (
                    <TableRow key={r.hash} data-testid={`cover-row-${r.hash}`}>
                      <TableCell>
                        {isCached ? (
                          <img
                            src={`/api/covers/${r.hash}`}
                            alt=""
                            className="w-10 h-14 object-cover rounded border border-slate-200"
                          />
                        ) : (
                          <div className="w-10 h-14 rounded border border-dashed border-red-300 flex items-center justify-center">
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-[10px] break-all leading-tight">
                        {r.original_url}
                        {r.reason ? (
                          <div className="text-[10px] text-red-600 mt-0.5">
                            corrupted: {r.reason}
                          </div>
                        ) : null}
                        {r.error ? (
                          <div className="text-[10px] text-red-600 mt-0.5">
                            error: {r.error}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-[11px] text-slate-600">
                        {r.size_bytes
                          ? `${(r.size_bytes / 1024).toFixed(1)} KB`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {r.reason ? (
                          <Badge variant="destructive" className="text-[10px]">
                            corrupted
                          </Badge>
                        ) : r.status === "cached" ? (
                          <Badge className="bg-emerald-600 text-[10px]">passed</Badge>
                        ) : (
                          <Badge variant="destructive" className="text-[10px]">
                            failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDeleteOne(r.hash)}
                          className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                          data-testid={`cover-row-delete-${r.hash}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between text-[11px] text-slate-500 sm:justify-between">
          <span>{rows.length} of {total} shown</span>
          <Button size="sm" variant="ghost" onClick={reload} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh list
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
